import React from "react";
import { type FormikProps } from "formik";
import { type LandingPageFormFields, type LandingPageFormProps } from "../types";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";
import { ValidatedForm } from "components/forms";
import { connect, type ConnectedProps } from "react-redux";
import { bindActionCreators } from "redux";
import { bindAction } from "interfaces/redux";
import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { campaignValidationSelector } from "../../state/selectors";
import * as validationActions from "../../state/thunks/campaignValidationThunk";
import { setInitialized, setIsValid } from "../../state/slices/campaignValidationSlice";
import { useFormikValidation } from "features/Library/Common/Hooks/useFormikValidationHook";
import WysiwygEditor from "components/WysiwygEditor/WysiwygEditor";

export type LandingPageFormPropsAll = FormikProps<LandingPageFormFields> &
  LandingPageFormProps &
  PropsFromRedux & { campaignId: number };

const LandingPageForm = (props: LandingPageFormPropsAll) => {
  const validationStep = "landingPageValidation";
  const {
    values,
    isLoading,
    isLoaded,
    onBlur,
    disabled,
    errors,
    activeErrors,
    validateActions,
    isFormValid,
    resolved,
    touchedSnapshot,
    initialized,
    campaignId,
  } = props;

  const { setActiveErrorsAction, checkForResolvedErrors } = validateActions;

  const formik = extractFormikProps<LandingPageFormFields, LandingPageFormPropsAll>(props);
  const nameof = nameofFactory<LandingPageFormFields>();

  useFormikValidation(
    { errors, resolved, initialized, activeErrors, isFormValid },
    {
      setInitializedAction: (isInitialized) => props.setInitialized({ validationStep, isInitialized }),
      setActiveErrors: (errors) => setActiveErrorsAction(validationStep, errors),
      setIsValidAction: (isValid) => props.setIsValid({ validationStep, isValid }),
      checkForResolvedErrors: (errors) => checkForResolvedErrors(validationStep, errors),
    },
  );

  return (
    <ValidatedForm
      dirty={props.dirty}
      disablePreventTransitionPrompt
      isLoading={isLoading || !isLoaded}
      formWidthComputer={6}
      withoutFormik
      fullsize
    >
      <WysiwygEditor
        {...formik}
        label="Body"
        value={values.html}
        defaultValue={formik.initialValues.html}
        propertyName={nameof("html")}
        placeholder="Enter Body"
        touched={!resolved ? touchedSnapshot : formik.touched}
        onBlur={onBlur}
        markAsRequired
        disabled={disabled}
        campaignId={campaignId}
        entityName={"landing-page"}
      />
    </ValidatedForm>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    ...campaignValidationSelector(state).landingPageValidation,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  validateActions: bindActionCreators(validationActions, dispatch),
  setIsValid: bindAction(setIsValid, dispatch),
  setInitialized: bindAction(setInitialized, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const ConnectedComponent = connector(LandingPageForm);
export default ConnectedComponent;
